import { useState } from "react";
import { ExternalLink, Layers, X } from "lucide-react";
import { DtVersionSelection, LinkedDtProject } from "../types";
import {
  DT_VERSION_ALL,
  formatBomImport,
  sanitizeDtVersions,
} from "../utils/dtVersions";
import { dependencyTrackProjectUrl } from "../utils/env";

interface DtVersionSelectModalProps {
  projects: LinkedDtProject[];
  selection: DtVersionSelection;
  onApply: (selection: DtVersionSelection) => void;
  onClose: () => void;
}

function versionLabel(version: string): string {
  return version || "unversioned";
}

function groupByApplication(
  projects: LinkedDtProject[],
): [string, LinkedDtProject[]][] {
  const groups = new Map<string, LinkedDtProject[]>();
  for (const project of projects) {
    const key = project.applicationName ?? "";
    groups.set(key, [...(groups.get(key) ?? []), project]);
  }
  return [...groups.entries()];
}

export default function DtVersionSelectModal({
  projects,
  selection,
  onApply,
  onClose,
}: DtVersionSelectModalProps) {
  const [draft, setDraft] = useState<DtVersionSelection>(() =>
    sanitizeDtVersions(selection, projects),
  );

  const setChoice = (linkId: string, value: string) => {
    setDraft((prev) => {
      const next = { ...prev };
      if (value) {
        next[linkId] = value;
      } else {
        delete next[linkId];
      }
      return next;
    });
  };

  const handleApply = () => {
    onApply(draft);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-lg mx-4 max-h-[85vh] overflow-y-auto">
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between rounded-t-lg">
          <div className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Select versions
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            title="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-5">
          {projects.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No Dependency-Track projects are linked.
            </p>
          ) : (
            groupByApplication(projects).map(([appName, group]) => (
              <div key={appName || "projects"} className="space-y-3">
                {appName && (
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    {appName}
                  </h3>
                )}
                {group.map((project) => {
                  const latest = project.versions.find(
                    (v) => v.uuid === project.latestUuid,
                  );
                  return (
                    <div key={project.linkId}>
                      <div className="flex items-center gap-1.5 mb-1">
                        <label
                          htmlFor={`dt-version-${project.linkId}`}
                          className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate"
                        >
                          {project.projectName || project.projectUuid}
                        </label>
                        <a
                          href={dependencyTrackProjectUrl(project.projectUuid)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                          title="Open in Dependency-Track"
                        >
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>
                      <select
                        id={`dt-version-${project.linkId}`}
                        value={draft[project.linkId] ?? ""}
                        onChange={(e) =>
                          setChoice(project.linkId, e.target.value)
                        }
                        className="input w-full text-sm"
                      >
                        <option value="">
                          Latest
                          {latest
                            ? ` (${versionLabel(latest.version)}, ${formatBomImport(latest.lastBomImport)})`
                            : ""}
                        </option>
                        {project.versions.length > 1 && (
                          <option value={DT_VERSION_ALL}>
                            All versions ({project.versions.length})
                          </option>
                        )}
                        {project.versions.map((v) => (
                          <option key={v.uuid} value={v.uuid}>
                            {versionLabel(v.version)} ·{" "}
                            {formatBomImport(v.lastBomImport)}
                            {v.isLatest ? " · latest" : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>

        <div className="sticky bottom-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-6 py-3 flex items-center justify-between rounded-b-lg">
          <button
            type="button"
            onClick={() => setDraft({})}
            disabled={Object.keys(draft).length === 0}
            className="btn btn-secondary text-sm disabled:opacity-40"
          >
            Reset all to latest
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary text-sm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApply}
              className="btn btn-primary text-sm"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
